import { IconNames } from '@dixa/lib-shared-habla';
import {
  getWidgetDefinitions,
  IconColorOptions,
  usePartnerIntegrationWidgetConfigurations,
  WidgetSettingsItem,
} from 'lib-integrations-partner-widgets';
import { useHasAccess } from 'lib-shared-access-control';
import { config } from 'lib-shared-backend-config';
import { CustomCardIntegrationSanitized } from 'lib-shared-types-api';
import { useMemo } from 'react';

import { integrationsApi } from 'apis/integrations';
import { getCardStyle } from 'components/shared/util/customCard';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import {
  SidebarCardKey,
  SidebarCardType,
  StaticSidebarCardId,
} from '../../conversationSidebar.types';
import { cardKey } from '../../utils/cardKey';

/**
 * Header metadata + identity for a card that can appear in the conversation
 * sidebar. Labels and icons mirror what each card renders in the conversation
 * (e.g. the memories card shows "Customer highlights", the knowledge card shows
 * "Articles") so both the sidebar and the admin page present them the same way.
 */
export type SidebarCardCatalogItem = {
  readonly key: SidebarCardKey;
  readonly cardType: SidebarCardType;
  readonly cardId: string;
  /** Natural fallback position (order the catalog builds cards in). */
  readonly defaultIndex: number;
  readonly label: string;
  readonly icon?: IconNames;
  readonly iconColors?: IconColorOptions;
  readonly iconUrl?: string;
  /** Underlying partner widget, present for `native` cards (used to render). */
  readonly widget?: WidgetSettingsItem;
  /** Underlying custom card, present for `custom` cards (used to render). */
  readonly customCard?: CustomCardIntegrationSanitized;
  /**
   * `true` for a native integration that is disabled in partner settings. Only
   * ever set when {@link UseSidebarCardCatalogOptions.includeDisabledIntegrations}
   * is on (i.e. the admin settings page).
   */
  readonly isDisabled?: boolean;
};

export type UseSidebarCardCatalogOptions = {
  /**
   * Include native integrations that are disabled in partner settings (flagged
   * via {@link SidebarCardCatalogItem.isDisabled}). Off by default so the live
   * sidebar never renders them; the admin settings page turns it on so admins
   * can still order/configure them.
   */
  readonly includeDisabledIntegrations?: boolean;
};

export type UseSidebarCardCatalogResult = {
  readonly cards: readonly SidebarCardCatalogItem[];
  readonly isLoading: boolean;
};

const STATIC_CARD_LABELS: Record<StaticSidebarCardId, string> = {
  contact: 'Contact',
  'contact-memories': 'Customer highlights',
  'custom-fields': 'Conversation attributes',
  history: 'Conversation history',
  tracking: 'Tracking',
  'knowledge-suggestions': 'Articles',
  elevio: 'Elevio',
};

const STATIC_CARD_ICONS: Record<StaticSidebarCardId, IconNames> = {
  contact: 'user',
  'contact-memories': 'lightbulb',
  'custom-fields': 'input',
  history: 'history',
  tracking: 'map-pin',
  'knowledge-suggestions': 'file-text',
  elevio: 'elevio',
};

// Most static cards let the Avatar derive its color from the icon name, which
// matches how the real cards render. The Articles card is the exception — its
// component passes an explicit grey, so we mirror it here.
const STATIC_CARD_ICON_COLORS: Partial<
  Record<StaticSidebarCardId, IconColorOptions>
> = {
  'knowledge-suggestions': {
    backgroundColor: 'var(--dixa-color-neutral-200)',
    contentColor: 'var(--dixa-typography-color-tertiary)',
  },
};

/**
 * The single source of truth for which cards can appear in the conversation
 * sidebar and their header metadata (identity, label, icon). Consumed by both
 * the sidebar ({@link useSidebarCards}, which layers the render function + any
 * per-conversation data gating on top) and the org admin settings page (which
 * only needs the headers). Keeping the list here guarantees the two views stay
 * in sync about which cards exist.
 *
 * Native widgets are listed when they are `enabled` in the org's partner
 * integration settings, unless `includeDisabledIntegrations` is set.
 */
export const useSidebarCardCatalog = (
  options?: UseSidebarCardCatalogOptions,
): UseSidebarCardCatalogResult => {
  const includeDisabledIntegrations =
    options?.includeDisabledIntegrations ?? false;
  const flags = useFeatureFlag([
    'contact-memories-conversation-view',
    'show-tracking-card',
    'elevio-suggestions',
    'knowledge-access',
  ]);
  const hasIntegrationAccess = useHasAccess('conversation.integration');

  const { widgets, isLoading: isWidgetsLoading } =
    usePartnerIntegrationWidgetConfigurations(config.url.partnerIntegrations, {
      skip: !hasIntegrationAccess,
    });

  const { customCards, isCustomCardsFetching } =
    integrationsApi.useReadCustomCardIntegrationsSanitizedQuery(undefined, {
      skip: !hasIntegrationAccess,
      selectFromResult: ({ data, isFetching }) => ({
        customCards: data?.data.custom_cards ?? [],
        isCustomCardsFetching: isFetching,
      }),
    });

  const cards = useMemo<SidebarCardCatalogItem[]>(() => {
    const items: SidebarCardCatalogItem[] = [];
    let order = 0;
    const nextIndex = () => order++;

    const staticCards: StaticSidebarCardId[] = [
      'contact',
      ...(flags.contactMemoriesConversationView
        ? (['contact-memories'] as const)
        : []),
      'custom-fields',
      'history',
      ...(flags.showTrackingCard ? (['tracking'] as const) : []),
      ...(flags.knowledgeAccess ? (['knowledge-suggestions'] as const) : []),
      ...(flags.elevioSuggestions ? (['elevio'] as const) : []),
    ];

    staticCards.forEach((cardId) =>
      items.push({
        key: cardKey({ cardType: 'static', cardId }),
        cardType: 'static',
        cardId,
        defaultIndex: nextIndex(),
        label: STATIC_CARD_LABELS[cardId],
        icon: STATIC_CARD_ICONS[cardId],
        iconColors: STATIC_CARD_ICON_COLORS[cardId],
      }),
    );

    const widgetDefinitions = getWidgetDefinitions();
    (widgets ?? [])
      .filter(
        (widget: WidgetSettingsItem) =>
          includeDisabledIntegrations || widget.enabled,
      )
      .forEach((widget: WidgetSettingsItem) => {
        const definition = widgetDefinitions[widget.type];
        items.push({
          key: cardKey({ cardType: 'native', cardId: widget.integrationKey }),
          cardType: 'native',
          cardId: widget.integrationKey,
          defaultIndex: nextIndex(),
          label: widget.name,
          icon: definition?.icon ?? 'plug',
          iconColors: definition?.iconColors,
          widget,
          isDisabled: !widget.enabled,
        });
      });

    customCards.forEach((card) => {
      const style = getCardStyle(card.integration_type, card.icon_url);
      items.push({
        key: cardKey({ cardType: 'custom', cardId: card.id }),
        cardType: 'custom',
        cardId: card.id,
        defaultIndex: nextIndex(),
        label: card.title || 'Custom card',
        icon: style.icon,
        iconUrl: style.iconUrl,
        customCard: card,
      });
    });

    return items;
  }, [flags, widgets, customCards, includeDisabledIntegrations]);

  return {
    cards,
    isLoading: isWidgetsLoading || isCustomCardsFetching,
  };
};
